import React, { useState, useMemo, useCallback } from 'react';
import DataTable from 'react-data-table-component';
import { MainLayout } from '@/components/layout/MainLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { 
  Plus, 
  Search, 
  Filter, 
  Calendar, 
  Stethoscope,
  Eye,
  Edit,
  Trash2,
  FileDown,
  User,
  AlertTriangle,
  UserPlus
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { procedureService } from '@/services/procedureService';
import { patientService } from '@/services/patientService';
import { Procedure, ProcedureFilters, ProcedureData } from '@/types/procedure.types';
import { Patient } from '@/types/patient.types';
import { ViewPatientDialog } from '@/components/forms/PatientList';
import { romanianLabels } from '@/utils/romanianLabels';
import { useToast } from '@/hooks/use-toast';
import { format, parseISO } from 'date-fns';
import { ro } from 'date-fns/locale';
import { ProcedureForm } from '@/components/forms/ProcedureForm';

export const ProceduresPage: React.FC = () => {
  const { toast } = useToast();

  // State management
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(20);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedPatientId, setSelectedPatientId] = useState<string>('');
  const [selectedProcedureTypeId, setSelectedProcedureTypeId] = useState<string>('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedProcedure, setSelectedProcedure] = useState<Procedure | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [viewProcedure, setViewProcedure] = useState<Procedure | null>(null);
  const [viewPatientDialogOpen, setViewPatientDialogOpen] = useState(false);
  const [viewPatient, setViewPatient] = useState<Patient | null>(null);
  
  // Add procedure functionality state
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isPatientSelectDialogOpen, setIsPatientSelectDialogOpen] = useState(false);
  const [selectedPatientForNew, setSelectedPatientForNew] = useState<Patient | null>(null);
  const [patientSearchQuery, setPatientSearchQuery] = useState('');

  // Build filters object
  const filters: ProcedureFilters = useMemo(() => ({
    page: currentPage,
    limit: pageSize,
    search: searchQuery || undefined,
    patient_id: selectedPatientId && selectedPatientId !== 'all' ? parseInt(selectedPatientId) : undefined,
    procedure_type_id: selectedProcedureTypeId && selectedProcedureTypeId !== 'all' ? parseInt(selectedProcedureTypeId) : undefined,
    date_from: dateFrom || undefined,
    date_to: dateTo || undefined,
    sortBy: 'procedure_date',
    sortOrder: 'desc'
  }), [currentPage, pageSize, searchQuery, selectedPatientId, selectedProcedureTypeId, dateFrom, dateTo]);

  // Data fetching
  const { 
    data: proceduresData, 
    isLoading: isProceduresLoading, 
    error: proceduresError, 
    refetch: refetchProcedures 
  } = useQuery({
    queryKey: ['procedures', filters],
    queryFn: () => procedureService.getProcedures(filters),
    placeholderData: (prev) => prev,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });

  // Fetch procedure types for filter dropdown
  const { data: procedureTypes } = useQuery({
    queryKey: ['procedure-types'],
    queryFn: () => procedureService.getProcedureTypes(),
    staleTime: 10 * 60 * 1000, // 10 minutes
  });

  // Fetch patients for filter dropdown
  const { data: patientsData } = useQuery({
    queryKey: ['patients-list'],
    queryFn: () => patientService.getPatients({ limit: 1000 }), // Get more patients for dropdown
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Event handlers
  const handleViewProcedure = useCallback((procedure: Procedure) => {
    setViewProcedure(procedure);
    setIsViewDialogOpen(true);
  }, []);

  const handleEditProcedure = useCallback((procedure: Procedure) => {
    setSelectedProcedure(procedure);
    setIsEditDialogOpen(true);
  }, []);

  const handleDeleteProcedure = useCallback((procedure: Procedure) => {
    setSelectedProcedure(procedure);
    setIsDeleteDialogOpen(true);
  }, []);

  // Table columns configuration
  const columns = useMemo(() => [
    {
      name: 'Pacient',
      selector: (row: Procedure) => row.patient_id,
      sortable: true,
      minWidth: '200px',
      cell: (row: Procedure) => {
        const patient = patientsData?.data?.find((p) => p.id === row.patient_id);

        const handlePatientClick = () => {
          if (!patient) return;
          setViewPatient(patient as Patient);
          setViewPatientDialogOpen(true);
        };

        return (
          <div className="flex flex-col">
            <button
              type="button"
              onClick={handlePatientClick}
              disabled={!patient}
              className="text-left font-medium text-sm text-primary hover:underline focus:outline-none disabled:text-gray-400"
            >
              {patient?.name || '-'}
            </button>
            <span className="text-xs text-gray-500">
              {patient?.patient_code || '-'}
            </span>
          </div>
        );
      },
    },
    {
      name: 'Tip procedură',
      selector: (row: Procedure) => row.procedure_type_name || '',
      sortable: true,
      minWidth: '150px',
      cell: (row: Procedure) => (
        <Badge variant="secondary" className="text-xs">
          {row.procedure_type_name}
        </Badge>
      ),
    },
    {
      name: 'Denumire procedură',
      selector: (row: Procedure) => row.procedure_name || '',
      sortable: true,
      minWidth: '200px',
      wrap: true,
      cell: (row: Procedure) => (
        <div className="text-sm">
          {row.procedure_name || '-'}
        </div>
      ),
    },
    {
      name: 'Data',
      selector: (row: Procedure) => row.procedure_date,
      sortable: true,
      minWidth: '110px',
      cell: (row: Procedure) => (
        <div className="flex items-center gap-1">
          <Calendar className="h-3 w-3 text-gray-500" />
          <span className="text-sm">
            {format(parseISO(row.procedure_date), 'dd.MM.yyyy', { locale: ro })}
          </span>
        </div>
      ),
    },
    {
      name: 'Rezultat 30 zile',
      selector: (row: Procedure) => row.outcome_30d || '',
      sortable: false,
      minWidth: '130px',
      cell: (row: Procedure) => {
        if (row.outcome_30d === null || row.outcome_30d === undefined || row.outcome_30d === '') {
          return <span className="text-gray-400">-</span>;
        }
        
        // Convert numeric values to display strings
        let displayValue = '';
        let variant: string = 'default';
        
        if (typeof row.outcome_30d === 'number') {
          // Handle numeric values from database
          switch(row.outcome_30d) {
            case 1:
              displayValue = 'Supraviețuitor';
              variant = 'default';
              break;
            case 2:
              displayValue = 'Decedat';
              variant = 'destructive';
              break;
            default:
              displayValue = 'Necunoscut';
              variant = 'secondary';
          }
        } else if (typeof row.outcome_30d === 'string') {
          // Handle string values
          displayValue = row.outcome_30d;
          variant = row.outcome_30d.toLowerCase().includes('deceda') || row.outcome_30d.toLowerCase().includes('deceased') ? 'destructive' :
                   row.outcome_30d.toLowerCase().includes('survivor') || row.outcome_30d.toLowerCase().includes('suprav') ? 'default' :
                   'secondary';
        }
        
        return (
          <Badge variant={variant as any} className="text-xs">
            {displayValue}
          </Badge>
        );
      },
    },
    {
      name: 'Executat de',
      selector: (row: Procedure) => row.performed_by_name || '',
      sortable: true,
      minWidth: '150px',
      cell: (row: Procedure) => (
        <div className="flex items-center gap-1">
          <User className="h-3 w-3 text-gray-500" />
          <span className="text-sm">
            {row.performed_by_name || '-'}
          </span>
        </div>
      ),
    },
    {
      id: 'actions',
      name: 'Acțiuni',
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
      minWidth: '120px',
      cell: (row: Procedure) => (
        <div className="flex gap-1">
          <Button
            variant="ghost"
            size="sm"
            className="h-8 w-8 p-0"
            onClick={() => handleViewProcedure(row)}
          >
            <Eye className="h-3 w-3" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="h-8 w-8 p-0"
            onClick={() => handleEditProcedure(row)}
          >
            <Edit className="h-3 w-3" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="h-8 w-8 p-0 text-red-600 hover:text-red-700"
            onClick={() => handleDeleteProcedure(row)}
          >
            <Trash2 className="h-3 w-3" />
          </Button>
        </div>
      ),
    },
  ], [patientsData?.data, handleViewProcedure, handleEditProcedure, handleDeleteProcedure]);

  // Event handlers
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1);
    refetchProcedures();
  };

  const handleClearFilters = () => {
    setSearchQuery('');
    setSelectedPatientId('');
    setSelectedProcedureTypeId('');
    setDateFrom('');
    setDateTo('');
    setCurrentPage(1);
  };

  const handleConfirmDelete = async () => {
    if (!selectedProcedure?.id) return;

    try {
      await procedureService.deleteProcedure(selectedProcedure.id);
      toast({
        title: 'Succes',
        description: 'Procedura a fost ștearsă cu succes.',
      });
      refetchProcedures();
    } catch (error) {
      toast({
        title: 'Eroare',
        description: 'Nu s-a putut șterge procedura. Încercați din nou.',
        variant: 'destructive',
      });
    } finally {
      setIsDeleteDialogOpen(false);
      setSelectedProcedure(null);
    }
  };

  const handleAddNewProcedure = () => {
    setPatientSearchQuery('');
    setSelectedPatientForNew(null);
    setIsPatientSelectDialogOpen(true);
  };

  const handleSelectPatientForNew = (patient: Patient) => {
    setSelectedPatientForNew(patient);
    setIsPatientSelectDialogOpen(false);
    setIsAddDialogOpen(true);
  };

  const handleAddProcedureSubmit = async (data: ProcedureData) => {
    try {
      await procedureService.createProcedure(data);
      toast({
        title: 'Succes',
        description: 'Procedura a fost adăugată cu succes.',
      });
      refetchProcedures();
      setIsAddDialogOpen(false);
      setSelectedPatientForNew(null);
    } catch (error: any) {
      toast({
        title: 'Eroare',
        description: error.message || 'Nu s-a putut adăuga procedura.',
        variant: 'destructive',
      });
      throw error;
    }
  };

  const handleExportData = async () => {
    try {
      if (!proceduresData?.data?.length) {
        toast({
          title: 'Atenție',
          description: 'Nu există date pentru export.',
        });
        return;
      }

      // Create CSV content
      const csvHeaders = [
        'Cod pacient',
        'Nume pacient',
        'Tip procedură',
        'Denumire procedură',
        'Data',
        'Rezultat 30 zile',
        'Executat de',
        'Spital'
      ];

      const csvRows = proceduresData.data.map(procedure => {
        const patient = patientsData?.data?.find(p => p.id === procedure.patient_id);
        return [
          patient?.patient_code || '-',
          patient?.name || '-',
          procedure.procedure_type_name || '-',
          procedure.procedure_name || '-',
          format(parseISO(procedure.procedure_date), 'dd.MM.yyyy', { locale: ro }),
          procedure.outcome_30d || '-',
          procedure.performed_by_name || '-',
          procedure.hospital_name || '-'
        ];
      });

      // Convert to CSV string
      const csvContent = [
        csvHeaders.join(','),
        ...csvRows.map(row => row.map(field => `"${field}"`).join(','))
      ].join('\n');

      // Create blob and download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `proceduri_${format(new Date(), 'yyyy-MM-dd')}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast({
        title: 'Succes',
        description: 'Datele au fost exportate cu succes.',
      });
    } catch (error) {
      toast({
        title: 'Eroare',
        description: 'Nu s-au putut exporta datele.',
        variant: 'destructive',
      });
    }
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  // Custom pagination component
  const paginationComponentOptions = {
    rowsPerPageText: romanianLabels.pagination.rowsPerPage,
    rangeSeparatorText: 'din',
    selectAllRowsItem: true,
    selectAllRowsItemText: 'Toate',
  };

  return (
    <MainLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
              <Stethoscope className="h-8 w-8" />
              Proceduri
            </h1>
            <p className="text-muted-foreground">
              Gestionați toate procedurile efectuate în spital
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleExportData}>
              <FileDown className="mr-2 h-4 w-4" />
              Export
            </Button>
            <Button onClick={handleAddNewProcedure}>
              <Plus className="mr-2 h-4 w-4" />
              Adaugă proceduri cardiovasculare
            </Button>
          </div>
        </div>

        {/* Filters */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2">
                <Filter className="h-5 w-5" />
                Filtre și căutare
              </CardTitle>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowFilters(!showFilters)}
              >
                {showFilters ? 'Ascunde filtre' : 'Afișează filtre'}
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Basic search */}
            <form onSubmit={handleSearch} className="flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  type="search"
                  placeholder="Căutați după numele pacientului sau procedura..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
              <Button type="submit" variant="secondary">
                <Search className="mr-2 h-4 w-4" />
                Caută
              </Button>
            </form>

            {/* Advanced filters */}
            {showFilters && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 pt-4 border-t">
                {/* Patient selector */}
                <div className="space-y-2">
                  <Label htmlFor="patient-select">Pacient</Label>
                  <Select
                    value={selectedPatientId}
                    onValueChange={setSelectedPatientId}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selectează pacient" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Toți pacienții</SelectItem>
                      {patientsData?.data?.map((patient: Patient) => (
                        <SelectItem key={patient.id} value={patient.id.toString()}>
                          {patient.name} ({patient.patient_code})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Procedure type selector */}
                <div className="space-y-2">
                  <Label htmlFor="type-select">Tip procedură</Label>
                  <Select
                    value={selectedProcedureTypeId}
                    onValueChange={setSelectedProcedureTypeId}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selectează tip" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Toate tipurile</SelectItem>
                      {procedureTypes?.map((type) => (
                        <SelectItem key={type.id} value={type.id.toString()}>
                          {type.name_ro}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Date from */}
                <div className="space-y-2">
                  <Label htmlFor="date-from">Data de la</Label>
                  <div className="relative">
                    <Calendar className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      type="date"
                      id="date-from"
                      value={dateFrom}
                      onChange={(e) => setDateFrom(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                </div>

                {/* Date to */}
                <div className="space-y-2">
                  <Label htmlFor="date-to">Data până la</Label>
                  <div className="relative">
                    <Calendar className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      type="date"
                      id="date-to"
                      value={dateTo}
                      onChange={(e) => setDateTo(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                </div>

                {/* Clear filters button */}
                <div className="flex items-end">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleClearFilters}
                    className="w-full"
                  >
                    Șterge filtre
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Data Table */}
        <Card>
          <CardContent className="p-0">
            {proceduresError ? (
              <Alert className="m-6">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>
                  Eroare la încărcarea procedurilor: {proceduresError.message}
                </AlertDescription>
              </Alert>
            ) : (
              <div className="procedures-table-sticky-actions">
                <style>{`
                  .procedures-table-sticky-actions [data-column-id="actions"] {
                    position: sticky;
                    right: 0;
                    z-index: 2;
                    background-color: #ffffff;
                    box-shadow: -2px 0 4px -2px rgba(0, 0, 0, 0.15);
                  }
                  .procedures-table-sticky-actions .rdt_TableCol[data-column-id="actions"] {
                    background-color: #f8f9fa;
                  }
                `}</style>
                <DataTable
                columns={columns}
                data={proceduresData?.data || []}
                progressPending={isProceduresLoading}
                progressComponent={
                  <div className="p-8 space-y-3">
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-4 w-3/4" />
                    <Skeleton className="h-4 w-1/2" />
                  </div>
                }
                pagination
                paginationServer
                paginationTotalRows={proceduresData?.total || 0}
                paginationPerPage={pageSize}
                paginationRowsPerPageOptions={[10, 20, 50, 100]}
                paginationComponentOptions={paginationComponentOptions}
                onChangePage={handlePageChange}
                onChangeRowsPerPage={(newPageSize) => {
                  // TODO: Implement dynamic page size change
                }}
                highlightOnHover
                pointerOnHover
                responsive
                striped
                noDataComponent={
                  <div className="py-24 text-center text-gray-500">
                    <Stethoscope className="mx-auto h-12 w-12 text-gray-300" />
                    <div className="mt-4">
                      <h3 className="text-lg font-semibold">Nicio procedură găsită</h3>
                      <p className="text-sm">
                        Nu există proceduri care să corespundă criteriilor de căutare.
                      </p>
                    </div>
                  </div>
                }
                customStyles={{
                  headRow: {
                    style: {
                      backgroundColor: '#f8f9fa',
                      borderBottom: '1px solid #dee2e6',
                    },
                  },
                  headCells: {
                    style: {
                      fontSize: '14px',
                      fontWeight: '600',
                      color: '#374151',
                      padding: '12px 8px',
                    },
                  },
                  cells: {
                    style: {
                      padding: '12px 8px',
                      fontSize: '14px',
                    },
                  },
                }}
                />
              </div>
            )}
          </CardContent>
        </Card>

        {/* Delete Confirmation Dialog */}
        <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-600">
                <AlertTriangle className="h-5 w-5" />
                Confirmare ștergere
              </DialogTitle>
              <DialogDescription>
                Sunteți sigur că doriți să ștergeți această procedură? Această acțiune nu poate fi anulată.
                {selectedProcedure && (
                  <div className="mt-2 p-3 bg-gray-50 rounded-md">
                    <strong>Procedură:</strong> {selectedProcedure.procedure_name}<br />
                    <strong>Data:</strong> {format(parseISO(selectedProcedure.procedure_date), 'dd.MM.yyyy', { locale: ro })}
                  </div>
                )}
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setIsDeleteDialogOpen(false)}
              >
                Anulează
              </Button>
              <Button
                variant="destructive"
                onClick={handleConfirmDelete}
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Șterge
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <ViewPatientDialog
          patient={viewPatient}
          isOpen={viewPatientDialogOpen}
          onClose={() => {
            setViewPatientDialogOpen(false);
            setViewPatient(null);
          }}
        />

        {/* View Procedure Dialog */}
        <Dialog open={isViewDialogOpen} onOpenChange={setIsViewDialogOpen}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Eye className="h-5 w-5" />
                Vizualizare procedură
              </DialogTitle>
            </DialogHeader>
            {viewProcedure && (
              <div className="space-y-6">
                {/* Patient Information */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Informații pacient</CardTitle>
                  </CardHeader>
                  <CardContent className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm font-medium text-gray-600">Nume pacient</Label>
                      <p className="text-sm font-medium">
                        {patientsData?.data?.find(p => p.id === viewProcedure.patient_id)?.name || '-'}
                      </p>
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-gray-600">Cod pacient</Label>
                      <p className="text-sm font-medium">
                        {patientsData?.data?.find(p => p.id === viewProcedure.patient_id)?.patient_code || '-'}
                      </p>
                    </div>
                  </CardContent>
                </Card>

                {/* Procedure Information */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Detalii procedură</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-sm font-medium text-gray-600">Tip procedură</Label>
                        <Badge variant="secondary" className="mt-1">
                          {viewProcedure.procedure_type_name || '-'}
                        </Badge>
                      </div>
                      <div>
                        <Label className="text-sm font-medium text-gray-600">Data</Label>
                        <div className="flex items-center gap-1 mt-1">
                          <Calendar className="h-4 w-4 text-gray-500" />
                          <p className="text-sm">
                            {format(parseISO(viewProcedure.procedure_date), 'dd MMMM yyyy', { locale: ro })}
                          </p>
                        </div>
                      </div>
                    </div>
                    
                    <div>
                      <Label className="text-sm font-medium text-gray-600">Denumire procedură</Label>
                      <p className="text-sm mt-1">{viewProcedure.procedure_name || '-'}</p>
                    </div>
                  </CardContent>
                </Card>

                {/* Results and Complications */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Rezultate și complicații</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-sm font-medium text-gray-600">Rezultat 30 zile</Label>
                        <div className="mt-1">
                          {viewProcedure.outcome_30d !== null && viewProcedure.outcome_30d !== undefined && viewProcedure.outcome_30d !== '' ? (
                            <Badge variant={
                              (typeof viewProcedure.outcome_30d === 'number' && viewProcedure.outcome_30d === 2) ||
                              (typeof viewProcedure.outcome_30d === 'string' && viewProcedure.outcome_30d.toLowerCase().includes('deceda'))
                                ? 'destructive' : 'default'
                            }>
                              {typeof viewProcedure.outcome_30d === 'number' 
                                ? viewProcedure.outcome_30d === 1 
                                  ? 'Supraviețuitor' 
                                  : viewProcedure.outcome_30d === 2 
                                    ? 'Decedat' 
                                    : 'Necunoscut'
                                : viewProcedure.outcome_30d
                              }
                            </Badge>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </div>
                      </div>
                      <div>
                        <Label className="text-sm font-medium text-gray-600">Reintervenție 1 an</Label>
                        <div className="mt-1">
                          {viewProcedure.reintervention_1y !== null && viewProcedure.reintervention_1y !== undefined && viewProcedure.reintervention_1y !== '' ? (
                            <Badge variant="outline">
                              {typeof viewProcedure.reintervention_1y === 'number'
                                ? viewProcedure.reintervention_1y === 0
                                  ? 'Nu'
                                  : viewProcedure.reintervention_1y === 1
                                    ? 'Da'
                                    : 'Necunoscut'
                                : viewProcedure.reintervention_1y
                              }
                            </Badge>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    {viewProcedure.complications && (
                      <div>
                        <Label className="text-sm font-medium text-gray-600">Complicații</Label>
                        <p className="text-sm mt-1 p-3 bg-gray-50 rounded-md">{viewProcedure.complications}</p>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Notes */}
                {(viewProcedure.notes || viewProcedure.note) && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg">Observații</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      {viewProcedure.notes && (
                        <div>
                          <Label className="text-sm font-medium text-gray-600">Observații administrative</Label>
                          <p className="text-sm mt-1 p-3 bg-gray-50 rounded-md">{viewProcedure.notes}</p>
                        </div>
                      )}
                      {viewProcedure.note && (
                        <div>
                          <Label className="text-sm font-medium text-gray-600">Note medicale</Label>
                          <p className="text-sm mt-1 p-3 bg-gray-50 rounded-md">{viewProcedure.note}</p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                )}

                {/* Additional Information */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Informații suplimentare</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-sm font-medium text-gray-600">Executat de</Label>
                        <div className="flex items-center gap-1 mt-1">
                          <User className="h-4 w-4 text-gray-500" />
                          <p className="text-sm">{viewProcedure.performed_by_name || '-'}</p>
                        </div>
                      </div>
                      <div>
                        <Label className="text-sm font-medium text-gray-600">Spital</Label>
                        <p className="text-sm mt-1">{viewProcedure.hospital_name || '-'}</p>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-sm font-medium text-gray-600">Creat la</Label>
                        <p className="text-sm mt-1">
                          {viewProcedure.created_at 
                            ? format(parseISO(viewProcedure.created_at), 'dd.MM.yyyy HH:mm', { locale: ro })
                            : '-'
                          }
                        </p>
                      </div>
                      <div>
                        <Label className="text-sm font-medium text-gray-600">Actualizat la</Label>
                        <p className="text-sm mt-1">
                          {viewProcedure.updated_at 
                            ? format(parseISO(viewProcedure.updated_at), 'dd.MM.yyyy HH:mm', { locale: ro })
                            : '-'
                          }
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsViewDialogOpen(false)}>
                Închide
              </Button>
              <Button onClick={() => {
                setIsViewDialogOpen(false);
                if (viewProcedure) {
                  handleEditProcedure(viewProcedure);
                }
              }}>
                <Edit className="mr-2 h-4 w-4" />
                Editează
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Procedure Dialog */}
        <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Edit className="h-5 w-5" />
                Editare procedură
              </DialogTitle>
            </DialogHeader>
            {selectedProcedure && (
              <ProcedureForm
                patient={patientsData?.data?.find(p => p.id === selectedProcedure.patient_id) || { 
                  id: selectedProcedure.patient_id, 
                  name: 'Pacient necunoscut', 
                  patient_code: '-' 
                } as Patient}
                procedure={selectedProcedure}
                onSubmit={async (data: ProcedureData) => {
                  try {
                    if (selectedProcedure.id) {
                      await procedureService.updateProcedure(selectedProcedure.id, data);
                      toast({
                        title: 'Succes',
                        description: 'Procedura a fost actualizată cu succes.',
                      });
                      refetchProcedures();
                      setIsEditDialogOpen(false);
                      setSelectedProcedure(null);
                    }
                  } catch (error: any) {
                    toast({
                      title: 'Eroare',
                      description: error.message || 'Nu s-a putut actualiza procedura.',
                      variant: 'destructive',
                    });
                    throw error;
                  }
                }}
                onCancel={() => {
                  setIsEditDialogOpen(false);
                  setSelectedProcedure(null);
                }}
              />
            )}
          </DialogContent>
        </Dialog>

        {/* Patient Selection Dialog */}
        <Dialog open={isPatientSelectDialogOpen} onOpenChange={setIsPatientSelectDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh]">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <UserPlus className="h-5 w-5" />
                Selectează pacient
              </DialogTitle>
              <DialogDescription>
                Selectați pacientul pentru care doriți să adăugați o procedură nouă.
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4">
              {/* Patient Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  type="search"
                  placeholder="Căutați după nume sau cod pacient..."
                  value={patientSearchQuery}
                  onChange={(e) => setPatientSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>

              {/* Patients List */}
              <div className="max-h-[400px] overflow-y-auto border rounded-md">
                {!patientsData?.data?.length ? (
                  <div className="p-8 text-center text-gray-500">
                    <User className="mx-auto h-12 w-12 text-gray-300" />
                    <div className="mt-4">
                      <h3 className="text-lg font-semibold">Niciun pacient găsit</h3>
                      <p className="text-sm">Nu există pacienți disponibili.</p>
                    </div>
                  </div>
                ) : (
                  patientsData.data
                    .filter(patient => 
                      !patientSearchQuery || 
                      patient.name.toLowerCase().includes(patientSearchQuery.toLowerCase()) ||
                      patient.patient_code.toLowerCase().includes(patientSearchQuery.toLowerCase())
                    )
                    .map((patient) => (
                      <div
                        key={patient.id}
                        className="flex items-center justify-between p-4 border-b last:border-b-0 hover:bg-gray-50"
                      >
                        <div className="flex flex-col">
                          <span className="font-medium text-sm">{patient.name}</span>
                          <span className="text-xs text-gray-500">Cod: {patient.patient_code}</span>
                          {patient.birth_date && (
                            <span className="text-xs text-gray-500">
                              Născut: {format(new Date(patient.birth_date), 'dd.MM.yyyy', { locale: ro })}
                            </span>
                          )}
                        </div>
                        <Button
                          size="sm"
                          onClick={() => handleSelectPatientForNew(patient)}
                        >
                          <Plus className="mr-2 h-4 w-4" />
                          Selectează
                        </Button>
                      </div>
                    ))
                )}
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setIsPatientSelectDialogOpen(false)}>
                Anulează
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Add Procedure Dialog */}
        <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Plus className="h-5 w-5" />
                Adăugare procedură nouă
              </DialogTitle>
            </DialogHeader>
            {selectedPatientForNew && (
              <ProcedureForm
                patient={selectedPatientForNew}
                procedure={null}
                onSubmit={handleAddProcedureSubmit}
                onCancel={() => {
                  setIsAddDialogOpen(false);
                  setSelectedPatientForNew(null);
                }}
              />
            )}
          </DialogContent>
        </Dialog>
      </div>
    </MainLayout>
  );
};
